import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { massagerAuthPath } from '@/shared/lib/apiPath';
import type {
  Clinic,
} from '@app-types/auth';
import type {
  Massager,
  MassagerLoginCredentials,
  MassagerLoginResponse,
} from '@/types/massagerAuth';

// ==================== Storage keys ====================

const TOKEN_KEY = 'massager_token';
const MASSAGER_KEY = 'massager_user';
const CLINIC_KEY = 'massager_clinic';
const SITE_KEY = 'massager_site';

// ==================== API service functions ====================

const login = async (credentials: MassagerLoginCredentials): Promise<MassagerLoginResponse> => {
  const site = credentials.site_type;
  if (!site) {
    throw new Error('site_type is required for massager login');
  }
  const response = await apiClient.post<MassagerLoginResponse>(
    massagerAuthPath(site, '/auth/login'),
    {
      clinic_number: credentials.clinic_number,
      login_id: credentials.login_id,
      password: credentials.password,
    }
  );
  return response.data;
};

const logout = async (site: string | undefined): Promise<void> => {
  try {
    if (!site) {
      throw new Error('site is required for massager logout');
    }
    await apiClient.post(massagerAuthPath(site, '/auth/logout'));
  } catch {
    // Ignore errors on logout — local storage is cleared either way.
  }
};

const getMassager = async (
  site: string
): Promise<{ massager: Massager; clinic: Clinic | null }> => {
  const response = await apiClient.get(massagerAuthPath(site, '/auth/user'));
  return response.data.data;
};

const refreshToken = async (
  site: string
): Promise<{ success: boolean; data?: { token: string } }> => {
  const response = await apiClient.post(massagerAuthPath(site, '/auth/refresh'));
  return response.data;
};

const forgotPassword = async (
  site: string,
  clinicNumber: string,
  loginId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post(massagerAuthPath(site, '/auth/forgot-password'), {
    clinic_number: clinicNumber,
    login_id: loginId,
  });
  return response.data;
};

const validateResetToken = async (
  site: string,
  token: string,
  clinicNumber?: string,
  loginId?: string
): Promise<{ success: boolean; data?: { valid: boolean } }> => {
  const response = await apiClient.post(massagerAuthPath(site, '/auth/validate-reset-token'), {
    token,
    clinic_number: clinicNumber,
    login_id: loginId,
  });
  return response.data;
};

const resetPassword = async (
  site: string,
  token: string,
  clinicNumber: string,
  loginId: string,
  password: string,
  passwordConfirmation: string
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post(massagerAuthPath(site, '/auth/reset-password'), {
    token,
    clinic_number: clinicNumber,
    login_id: loginId,
    password,
    password_confirmation: passwordConfirmation,
  });
  return response.data;
};

// ==================== Storage helpers ====================

export const storeMassagerAuth = (
  data: MassagerLoginResponse['data'],
  site: string
): void => {
  localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(MASSAGER_KEY, JSON.stringify(data.massager));
  localStorage.setItem(CLINIC_KEY, JSON.stringify(data.clinic));
  localStorage.setItem(SITE_KEY, site);
};

export const storeMassagerToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const getStoredMassagerToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const clearMassagerStorage = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(MASSAGER_KEY);
  localStorage.removeItem(CLINIC_KEY);
  localStorage.removeItem(SITE_KEY);
};

// ==================== React Query hooks ====================

export const useMassagerLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: MassagerLoginCredentials) => login(credentials),
    onSuccess: (data: MassagerLoginResponse, variables) => {
      if (data.success && data.data && variables.site_type) {
        storeMassagerAuth(data.data, variables.site_type);
        queryClient.invalidateQueries({ queryKey: ['massager-auth', 'user'] });
      }
    },
  });
};

export const useMassagerLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ site }: { site: string }) => logout(site),
    onSuccess: () => {
      clearMassagerStorage();
      queryClient.clear();
    },
  });
};

export const useMassagerGetUser = (site?: string) => {
  return useQuery({
    queryKey: ['massager-auth', 'user', site],
    queryFn: () => {
      if (!site) {
        throw new Error('site is required to fetch massager');
      }
      return getMassager(site);
    },
    enabled: !!getStoredMassagerToken() && !!site,
    staleTime: 5 * 60 * 1000,
  });
};

export const useMassagerRefreshToken = () => {
  return useMutation({
    mutationFn: ({ site }: { site: string }) => refreshToken(site),
    onSuccess: data => {
      if (data.success && data.data) {
        storeMassagerToken(data.data.token);
      }
    },
  });
};

export const useMassagerForgotPassword = () => {
  return useMutation({
    mutationFn: ({
      site,
      clinicNumber,
      loginId,
    }: {
      site: string;
      clinicNumber: string;
      loginId: string;
    }) => forgotPassword(site, clinicNumber, loginId),
    // Avoid global "データが見つかりません。" toast — page handles errors inline.
    meta: { suppressNotFoundToast: true },
  });
};

export const useMassagerValidateResetToken = () => {
  return useMutation({
    mutationFn: ({
      site,
      token,
      clinicNumber,
      loginId,
    }: {
      site: string;
      token: string;
      clinicNumber?: string;
      loginId?: string;
    }) => validateResetToken(site, token, clinicNumber, loginId),
  });
};

export const useMassagerResetPassword = () => {
  return useMutation({
    mutationFn: ({
      site,
      token,
      clinicNumber,
      loginId,
      password,
      passwordConfirmation,
    }: {
      site: string;
      token: string;
      clinicNumber: string;
      loginId: string;
      password: string;
      passwordConfirmation: string;
    }) => resetPassword(site, token, clinicNumber, loginId, password, passwordConfirmation),
    meta: { suppressNotFoundToast: true },
  });
};
